import { EnhancedScopeData } from '@ardoq/data-model';
import type { FormatUserFieldFn } from '@ardoq/scope-data';
import {
  FilterInterfaceFilter,
  FormattingFilter,
  GroupingRule,
} from '@ardoq/api-types';
import type { I18nLocale } from '@ardoq/i18n';
import {
  getGraphInterfaceForEnhancedScopeData,
  getGraphInterfaceWithModelInterfaces,
} from './graphInterface/graphInterface';
import {
  getLabelsInterfaceForEnhancedScopeData,
  getLabelsInterfaceWithModelInterfaces,
} from './labelsInterface/labelsInterface';
import {
  getRepresentationInterfaceForEnhancedScopeData,
  getRepresentationInterfaceWithModelInterfaces,
  type RepresentationInterface,
} from './representationInterface/representationInterface';

export type ScopeDataParams = {
  enhancedScopeData: EnhancedScopeData;
  formattingFilters: (FormattingFilter | FilterInterfaceFilter)[];
  groupingRules: GroupingRule[];
  i18nLocale: I18nLocale;
  formatUserFieldFn?: FormatUserFieldFn;
};
type GetVisualizationInterfaceArgs<T> = {
  scopeDataParams?: ScopeDataParams;
  getViewSpecificInterface: (args?: ScopeDataParams) => T;
};

export type VisualizationInterface<T> = T &
  ReturnType<typeof getGraphInterfaceWithModelInterfaces> &
  ReturnType<typeof getLabelsInterfaceWithModelInterfaces> &
  RepresentationInterface;

/**
 * Feed it with scope data params (grouping rules and conditional formatting), and use the
 * get[concern]InterfaceForEnhancedScopeData functions to avoid the BB models
 *
 * @returns methods that are needed for composing the view model
 */
const getVisualizationInterface = <T>({
  scopeDataParams,
  getViewSpecificInterface,
}: GetVisualizationInterfaceArgs<T>): VisualizationInterface<T> => {
  if (scopeDataParams) {
    const {
      enhancedScopeData,
      formattingFilters,
      groupingRules,
      i18nLocale,
      formatUserFieldFn,
    } = scopeDataParams;
    const graphInterface = getGraphInterfaceForEnhancedScopeData({
      enhancedScopeData,
      conditionalFormatting: formattingFilters,
      groupingRules,
      i18nLocale,
    });
    const labelsInterface = getLabelsInterfaceForEnhancedScopeData({
      enhancedScopeData,
      formattingFilters,
      i18nLocale,
      formatUserFieldFn,
    });
    const representationInterface =
      getRepresentationInterfaceForEnhancedScopeData({
        enhancedScopeData,
        formattingFilters,
      });
    const viewSpecificInterface = getViewSpecificInterface?.(scopeDataParams);
    return {
      ...graphInterface,
      ...labelsInterface,
      ...representationInterface,
      ...viewSpecificInterface,
    };
  }
  const graphInterface = getGraphInterfaceWithModelInterfaces();
  const labelsInterface = getLabelsInterfaceWithModelInterfaces();
  const representationInterface =
    getRepresentationInterfaceWithModelInterfaces();
  const viewSpecificInterface = getViewSpecificInterface();
  return {
    ...graphInterface,
    ...labelsInterface,
    ...representationInterface,
    ...viewSpecificInterface,
  };
};

export { getVisualizationInterface };
